<script setup lang="ts">
import {
  apps,
  formatting,
  LegacyOrder,
  legacyOrderKey,
  LegacyOrderRow,
  useContext,
  useLocalization
} from '@hantera/portal-app';
import {InspectorLayout, type InspectorProvider, type InspectorTab} from '@hantera/design-system';
import {computed, onMounted, provide, reactive, Ref, ref, watch, watchEffect} from 'vue';
import useBackend from '@/composition/useBackend';
import LegacyOrderHeader from './LegacyOrderHeader.vue';
import Deliveries from './Deliveries.vue';
import Payments from './Payments.vue';
import Invoices from '@/components/LegacyOrder/Invoices.vue';
import Rmas from './Rmas.vue';
import Timeline from './Timeline.vue';
import {NewRowAdjustment, newRowAdjustmentKey, orderRulesKey} from '@/components/LegacyOrder/order-keys';
import {EditableOrderRule} from '@/components/LegacyOrder/editable-order-adjustment';
import Decimal from 'decimal.js';
import Summary from "@/views/LegacyOrder/Summary.vue";
import useApps from "@/composition/use-apps";

const props = defineProps<{
  orderId: string
  selectedTab?: string
}>();

const emit = defineEmits<{
  (e: 'load', result: LegacyOrder): void
  (e: 'update:selectedTab', tab: string): void
  (e: 'openOrder', order: LegacyOrder): void
  (e: 'saveStateChanged', value: boolean): void
}>();

const orderId = computed(() => props.orderId)

const backend = useBackend();
const t = useLocalization();
const appsContext = useApps()
const context = useContext()
const order = ref<LegacyOrder>();
provide(legacyOrderKey, order as Ref<LegacyOrder>);
const paymentsElem = ref<typeof Payments>();

const serviceInspectorTabs = ref<InspectorTab[]>([])

const newRowAdjustment = ref<NewRowAdjustment>();
provide(newRowAdjustmentKey, newRowAdjustment);

const adjustments = ref<EditableOrderRule[]>([]);
provide(orderRulesKey, adjustments);
watchEffect(() => {
  adjustments.value = order.value?.rules.map(rule => {

    const affectedRows = order.value?.deliveries
      .reduce((rows, delivery) => {
        return [...rows, ...delivery.rows];
      }, <LegacyOrderRow[]>[])
      .filter(r => r.adjustments.findIndex(a => a.orderRule?.ruleId === rule.ruleId) >= 0);

    return <EditableOrderRule>{
      adjustmentId: rule.orderRuleId,
      editable: rule.sourceType === 'Manual' && !rule.fields.find(r => r.id === 'IsRmaBasedAdjustment'),
      name: rule.name,
      originalValue: rule.ruleTotal,
      value: rule.ruleTotal,
      affectedRows: affectedRows?.map(r => {
        const aValue = r.adjustments.filter(a => a.orderRule?.ruleId === rule.ruleId).reduce((total, adj) => {
          return total.add(adj.adjustmentTotal);
        }, new Decimal(0));

        return {
          rowId: r.rowId,
          originalValue: aValue,
          value: aValue,
          isRowBased: r.adjustments.findIndex(r => r.orderRule?.orderRuleId === rule.orderRuleId && r.fields.findIndex(f => f.id === 'RowBasedAdjustment' && f.value) >= 0) >= 0,
        };
      }),
    };
  }) ?? [];
});

watch(() => order.value?.draftChanges?.length ?? 0, (c) => {
  emit('saveStateChanged', c > 0);
}, {
  immediate: true,
})

const selectedTab = ref<string>(props.selectedTab || 'deliveries');
watch(() => props.selectedTab, val => {
  if (val) {
    selectedTab.value = val;
  }
});
watch(() => selectedTab.value, val => {
  emit('update:selectedTab', selectedTab.value);
});

onMounted(async () => {
  refresh();
});

async function refresh() {
  order.value = await backend.legacyOrders.get(orderId.value);
  if (order.value) {
    emit('load', order.value);
  }
}

const layoutProvider: InspectorProvider = reactive(<InspectorProvider>{
  title: '',
  labels: [],
  summary: Summary,
  selectedTab: selectedTab.value,
  tabs: [],
})

watchEffect(() => {
  if (!order.value) {
    return
  }

  layoutProvider.title = order.value.orderNumber
  layoutProvider.labels = [
    order.value.channelName,
    order.value.currencyCode,
    {
      label: formatting.date(order.value.createdUtc),
      tooltip: formatting.dateTime(order.value.createdUtc)
    },
  ]
})

watchEffect(() => {
  if (!order.value) {
    return
  }

  layoutProvider.tabs = [{
    id: 'deliveries',
    label: t('orderView.deliveriesTab'),
    tags: order.value.deliveries?.length > 0 ? [{
      style: 'primary',
      value: formatting.number(order.value.deliveries.length)
    }] : [],
    component: Deliveries
  }, {
    id: 'rmas',
    label: t('orderView.rmasTab'),
    tags: order.value.rmas?.length > 0 ? [{
      style: 'primary',
      value: formatting.number(order.value.rmas.length)
    }] : [],
    component: Rmas
  }, {
    id: 'invoices',
    label: t('orderView.invoicesTab'),
    tags: order.value.invoices?.length > 0 ? [{
      style: 'primary',
      value: formatting.number(order.value.invoices.length)
    }] : [],
    component: Invoices
  }, {
    id: 'payments',
    label: t('orderView.paymentsTab'),
    tags: order.value.payments?.length > 0 ? [{
      style: 'primary',
      value: formatting.number(order.value.payments.length)
    }] : [],
    component: Payments
  }, {
    id: 'timeline',
    label: t('orderView.timelineTab'),
    component: Timeline
  }, ...serviceInspectorTabs.value]
})

watch(() => order.value != undefined, () => {
  if (!order.value) {
    return
  }

  let serviceTabIdx = 0
  serviceInspectorTabs.value = []

  for (const provider of appsContext.getServices(apps.standardServices.legacyOrderView.tabProvider)) {

    const tabs = provider.build(context, order as Ref<LegacyOrder>)
    if (!Array.isArray(tabs)) {
      continue
    }

    for (const tab of tabs) {
      const tabId = `service${serviceTabIdx++}`

      const tabState = ref<Record<string, any>>()

      watch(() => context.currentView.value?.state?.tabs?.[tabId], (val) => {

        if (!val) {
          tabState.value = {}
          return
        }

        tabState.value = val
      }, { immediate: true, deep: true })

      watch(tabState, () => {
        if (!context.currentView.value?.state) {
          return
        }

        if (!context.currentView.value.state.tabs) {
          context.currentView.value.state.tabs = {}
        }

        context.currentView.value.state.tabs[tabId] = tabState.value
      }, { deep: true })

      const props = reactive({
        uri: tab.viewUri,
        state: tabState
      })

      serviceInspectorTabs.value.push({
        id: tabId,
        label: tab.label as any,
        tags: tab.tags as any,
        component: 'EmbeddedView',
        toolbar: tab.toolbar,
        props
      })
    }
  }
})

watchEffect(() => {
  if (!order.value) {
    return
  }

  let state: {
    label: string
    hue?: number
  } = {
    label: 'Pending'
  }

  if (order.value.state === 'Confirmed') {
    state = {
      label: 'Confirmed',
      hue: 133.42
    }
  } else if (order.value.state === 'Cancelled') {
    state = {
      label: 'Cancelled',
      hue: 18.87
    }
  }

  layoutProvider.state = state
})

watch(() => layoutProvider.selectedTab, () => {
  selectedTab.value = layoutProvider.selectedTab ?? 'deliveries'
})

</script>

<template>

  <InspectorLayout :provider="layoutProvider" v-if="order">
    <template #header>
      <LegacyOrderHeader />
    </template>
  </InspectorLayout>
  
</template>
